<template>
  <div class="pdf-preview" ref="pdfContainer">
    <jw-spin :loading="pdfStatus == 'loading'" class="pdf-loading" @scroll="handleScrollPdf">
      <div class="pdf-container">
        <template v-if="totalPages > 0">
          <div
            v-for="pageNum in totalPages"
            :key="pageNum"
            :data-page-num="pageNum"
            class="pdf-page-wrapper"
            :style="{
              width: `${Math.floor(getViewportObj[pageNum]?.width)}px`,
              height: `${Math.floor(getViewportObj[pageNum]?.height)}px`
            }"
          >
            <canvas :ref="(el) => (canvasRefs[pageNum] = el)" />
          </div>
        </template>

        <template v-if="totalPages == 0 && pdfStatus == 'done'">
          <div class="no-data">
            <jw-empty :image="noDataSvg" :description="$t('暂无数据')" />
          </div>
        </template>
      </div>
    </jw-spin>
  </div>
</template>
<script setup>
import { ref, computed, reactive } from 'vue';
import * as pdfjsLib from 'pdfjs-dist';
// 设置PDF.js worker路径
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
import noDataSvg from '@/assets/images/no-data-150.svg';
import useXlUpload from '@/hooks/upload';
import { useUploadStore } from '@/store';
const uploadStore = useUploadStore();
const PDF_CMAP_URL = '/pdfjs/cmaps/';

const { setUrl, getSignedUrl } = useXlUpload();
const props = defineProps({
  activeFile: {
    //当前文件
    type: Object,
    default: () => {}
  },
  scale: {
    //缩放比例
    type: Number,
    default: 1.0
  }
});
let pdfDocumentRaw = null; //pdf文件信息

const totalPages = ref(0); //总数
const rawViewportObj = reactive({}); // 保存每个页面的原始viewport（scale=1时的尺寸）
const getViewportObj = reactive({}); //pdf实时缩放时的文件信息
const canvasRefs = ref([]); //pdf canvas
const pdfStatus = ref(null); //pdf加载状态
const getUrl = async () => {
  if (props.activeFile?.filePath) {
    const filePath = props.activeFile.filePath;
    const signedUrl = await getSignedUrl(filePath);
    return signedUrl || setUrl(props.activeFile, 1);
  } else {
    return setUrl(props.activeFile, 1);
  }
};

const getScale = computed(() => {
  return props.scale;
});

// 渲染所有页面
const renderAllPages = async () => {
  for (let pageNum = 1; pageNum <= totalPages.value; pageNum++) {
    await renderPage(pageNum);
  }
};

// 渲染单个页面
const renderPage = async (pageNum) => {
  if (!pdfDocumentRaw) return;

  try {
    const page = await pdfDocumentRaw.getPage(pageNum);

    // 1. 获取并保存原始视图（scale=1）—— 关键：用于后续坐标转换
    const rawViewport = page.getViewport({ scale: 1 });
    rawViewportObj[pageNum] = rawViewport;

    const viewport = page.getViewport({ scale: getScale.value }); // 获取 PDF 页面原始尺寸
    const canvasElement = canvasRefs.value[pageNum];

    if (!canvasElement) return;
    getViewportObj[pageNum] = viewport;

    const context = canvasElement.getContext('2d');
    canvasElement.height = viewport.height;
    canvasElement.width = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    await page.render(renderContext).promise;
  } catch (error) {
    console.error(`渲染第 ${pageNum} 页时出错:`, error);
  }
};

// 获取pdf文件
const getPdfFile = async () => {
  pdfStatus.value = 'loading';

  pdfDocumentRaw = null;
  totalPages.value = 0;
  uploadStore.setCurrentPage(1);
  uploadStore.setTotalPages(totalPages.value);

  try {
    const getUrlValue = await getUrl();
    const loadingTask = pdfjsLib.getDocument({
      url: getUrlValue,
      cMapUrl: PDF_CMAP_URL,
      cMapPacked: true
    });
    pdfDocumentRaw = await loadingTask.promise;

    totalPages.value = pdfDocumentRaw.numPages;
    uploadStore.setTotalPages(totalPages.value);

    pdfStatus.value = 'done';
    renderAllPages();
  } catch (error) {
    pdfStatus.value = 'done';
    console.error('PDF加载错误:', error);
  }
};

// 改变页数
const pageChange = async (params) => {
  const { currentPage: pageNum, type } = params;
  if (pageNum < 1 || pageNum > totalPages.value) return;
  if (!pdfDocumentRaw) return;

  if (type === 'pageTo') {
    //跳转指定页
    await renderPage(pageNum);
  }
  scrollToPage(pageNum);
};

//操作项回调
const handleOpCb = (params) => {
  if (params.type === 'zoom') {
    //缩放

    renderAllPages();
  }
  if (params.type === 'pageChange' || params.type === 'pageTo') {
    //页面改变或跳转指定页
    pageChange(params);
  }
};
// 滚动到指定页面
const scrollToPage = (pageNum) => {
  const pageElementCanvas = canvasRefs.value[pageNum];
  if (pageElementCanvas && pageElementCanvas.parentElement) {
    pageElementCanvas.parentElement.scrollIntoView({ behavior: 'smooth' });
  }
};

// 滚动pdf时记录当前页码(页面看不见时再更新页码)

const handleScrollPdf = async (e) => {
  for (const page of document.querySelectorAll('.pdf-page-wrapper')) {
    if (Math.abs(e.target.scrollTop - page.offsetTop + 20 - page.clientHeight) < page.clientHeight) {
      // 记录当前页码

      uploadStore.setCurrentPage(Number(page.dataset.pageNum));
    }
  }
};

defineExpose({
  handleOpCb,
  getPdfFile
});
</script>

<style scoped lang="less">
@import './index.less';
</style>
